import { Location } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';

import { LocalizeParser, LocalizeRouterModule, LocalizeRouterSettings } from '@gilsdav/ngx-translate-router';
import { LocalizeRouterHttpLoader } from '@gilsdav/ngx-translate-router-http-loader';
import { TranslateService } from '@ngx-translate/core';

import { AuthGuard, TicketDeviceGuard } from '@ridango/components';

import { AccessGuard } from './access.guard';
import { AccountChangeEmailPageComponent } from './pages/account-change-email-page/account-change-email-page.component';
import { AccountChangePasswordPageComponent } from './pages/account-change-password-page/account-change-password-page.component';
import { AccountDetailPageComponent } from './pages/account-detail-page/account-detail-page.component';
import { AccountEditPageComponent } from './pages/account-edit-page/account-edit-page.component';
import { AccountParentPageComponent } from './pages/account-parent-page/account-parent-page.component';
import { BalancePageComponent } from './pages/balance-page/balance-page.component';
import { BankcardTicketsPageComponent } from './pages/bankcard-tickets-page/bankcard-tickets-page.component';
import { BikeGaragePageComponent } from './pages/bike-garage-page/bike-garage-page.component';
import { CustomerForgetLandingPageComponent } from './pages/customer-forget-landing-page/customer-forget-landing-page.component';
import { CustomerSupportPageComponent } from './pages/customer-support-page/customer-support-page.component';
import { EmailVerificationPageComponent } from './pages/email-verification-page/email-verification-page.component';
import { ExploreScheduleOnStopAdditionalInfoPageComponent } from './pages/explore-page/explore-schedule-on-stop-additional-info-page/explore-schedule-on-stop-additional-info-page.component';
import { HomePageComponent } from './pages/home-page/home-page.component';
import { IsicCardActivationPageComponent } from './pages/isic-card-activation-page/isic-card-activation-page.component';
import { LoginPageComponent } from './pages/login-page/login-page.component';
import { MyProfileParentPageComponent } from './pages/my-profile-parent-page/my-profile-parent-page.component';
import { PasswordRecoverPageComponent } from './pages/password-recover-page/password-recover-page.component';
import { PasswordRecoverVerificationPageComponent } from './pages/password-recover-verification-page/password-recover-verification-page.component';
import { PaymentResultPageComponent } from './pages/payment-result-page/payment-result-page.component';
import { PersonalizeParentPageComponent } from './pages/personalize-parent-page/personalize-parent-page.component';
import { PurchaseHistoryPageComponent } from './pages/purchase-history-page/purchase-history-page.component';
import { PurchasePageComponent } from './pages/purchase-page/purchase-page.component';
import { RegisterPageComponent } from './pages/register-page/register-page.component';
import { RegisterVerificationPageComponent } from './pages/register-verification-page/register-verification-page.component';
import { RelatedCardAddAndEditPageComponent } from './pages/related-card-add-and-edit-page/related-card-add-and-edit-page.component';
import { RelatedCardDetailPageComponent } from './pages/related-card-detail-page/related-card-detail-page.component';
import { RelatedCardListPageComponent } from './pages/related-card-list-page/related-card-list-page.component';
import { RelatedCardPersonalizePageComponent } from './pages/related-card-personalize-page/related-card-personalize-page.component';
import { ScheduleFavouritesPageComponent } from './pages/schedule-page/schedule-favourites-page/schedule-favourites-page.component';
import { ScheduleParentPageComponent } from './pages/schedule-page/schedule-parent-page/schedule-parent-page.component';
import { SchedulePlannerPageComponent } from './pages/schedule-page/schedule-planner-page/schedule-planner-page.component';
import { SchedulePlannerTripDetailsPageComponent } from './pages/schedule-page/schedule-planner-trip-details-page/schedule-planner-trip-details-page.component';
import { ScheduleRouteDirectionsPageComponent } from './pages/schedule-page/schedule-route-directions-page/schedule-route-directions-page.component';
import { ScheduleRouteStopDetailsPageComponent } from './pages/schedule-page/schedule-route-stop-details-page/schedule-route-stop-details-page.component';
import { ScheduleRouteStopsPageComponent } from './pages/schedule-page/schedule-route-stops-page/schedule-route-stops-page.component';
import { ScheduleRouteTripPageComponent } from './pages/schedule-page/schedule-route-trip-page/schedule-route-trip-page.component';
import { ScheduleRoutesPageComponent } from './pages/schedule-page/schedule-routes-page/schedule-routes-page.component';
import { ScheduleSearchPageComponent } from './pages/schedule-page/schedule-search-page/schedule-search-page.component';
import { SeutulippuPageComponent } from './pages/seutulippu-page/seutulippu-page.component';
import { SeutulippuRegisterPageComponent } from './pages/seutulippu-register-page/seutulippu-register-page.component';
import { ShoppingCartPageComponent } from './pages/shopping-cart-page/shopping-cart-page.component';
import { SwishConfirmationPageComponent } from './pages/swish-confirmation-page/swish-confirmation-page.component';
import { TermsPageComponent } from './pages/terms-page/terms-page.component';
import { TravelcardsAndTicketsParentPageComponent } from './pages/travelcards-and-tickets-parent-page/travelcards-and-tickets-parent-page.component';
import { ValidationHistoryPageComponent } from './pages/validation-history-page/validation-history-page.component';
import { WalletLoadingPageComponent } from './pages/wallet-loading-page/wallet-loading-page.component';
import { PendingChangesGuard } from './pending-changes.guard';
import { UnauthGuard } from './unauth.guard';
import { BookRideParentPageComponent } from './pages/book-ride-page/book-ride-parent-page/book-ride-parent-page.component';
import { BookRidePlanPageComponent } from './pages/book-ride-page/book-ride-plan-page/book-ride-plan-page.component';
import { BookRideSpecifyPageComponent } from './pages/book-ride-page/book-ride-specify-page/book-ride-specify-page.component';
import { BookRideReviewPageComponent } from './pages/book-ride-page/book-ride-review-page/book-ride-review-page.component';
import { BookingsPageComponent } from './pages/account-bookings-page/account-bookings-page.component';
import { RideHistoryPageComponent } from './pages/account-ride-history-page/ride-history-page.component';
import { AdyenPaymentPageComponent } from './pages/adyen-payment-page/adyen-payment-page.component';
import { TicketsParentPageComponent } from './pages/tickets-parent-page/tickets-parent-page.component';
import { AppAdyenPaymentComponent } from './pages/app-adyen-payment/app-adyen-payment.component';
import { OndemandTripsPageComponent } from './pages/ondemand-trips-page/ondemand-trips-page.component';
import { AccountCompaniesPageComponent } from './pages/account-companies-page/account-companies-page.component';
import { JourneyPlannerPageComponent } from './pages/journey-planner-page/journey-planner-page.component';
import { JourneyPlannerSearchComponent } from './pages/journey-planner-page/journey-planner-search/journey-planner-search.component';
import { JourneyPlannerResultsComponent } from './pages/journey-planner-page/journey-planner-results/journey-planner-results.component';
import { ExplorePageComponent } from './pages/explore-page/explore-page.component';
import { DisruptionMessagesPageComponent } from './pages/disruption-messages-page/disruption-messages-page.component';
import { TicketsPageComponent } from './pages/tickets-page/tickets-page.component';
import { AccountCompanyUsersListPageComponent } from './pages/account-company-users-list-page/account-company-users-list-page.component';
import { CompanyGuard } from './company.guard';
import { AccountCompanyUsersAddPageComponent } from './pages/account-company-users-add-page/account-company-users-add-page.component';
import { AccountCompanyEditPageComponent } from './pages/account-company-edit-page/account-company-edit-page.component';
import { NotificationSettingsPageComponent } from './pages/notification-settings-page/notification-settings-page.component';
import { TicketDetailsPageComponent } from './pages/ticket-details-page/ticket-details-page.component';
import { ExploreSearchPageComponent } from './pages/explore-page/explore-search-page/explore-search-page.component';
import { ExploreStopDetailsPageComponent } from './pages/explore-page/explore-stop-details-page/explore-stop-details-page.component';
import { ExploreTripDetailsPageComponent } from './pages/explore-page/explore-trip-details-page/explore-trip-details-page.component';
import { JourneyPlannerItineraryDetailsComponent } from './pages/journey-planner-page/journey-planner-itinerary-details/journey-planner-itinerary-details.component';
import { ExploreRoutePatternsPageComponent } from './pages/explore-page/explore-route-patterns-page/explore-route-patterns-page.component';
import { FeedbackPageComponent } from './pages/feedback-page/feedback-page.component';
import { ExploreScheduleOnStopPageComponent } from './pages/explore-page/explore-schedule-on-stop-page/explore-schedule-on-stop-page.component';
import { AccountCardsParentPageComponent } from './pages/account-cards-page/account-cards-parent-page.component';
import { CardsLandingPageComponent } from './pages/account-cards-page/cards-landing-page/cards-landing-page.component';
import { CardsDetailsPageComponent } from './pages/account-cards-page/cards-details-page/cards-details-page.component';
import { CardsEditPageComponent } from './pages/account-cards-page/cards-add-edit-page/cards-edit-page.component';
import { CardsAddPageComponent } from './pages/account-cards-page/cards-add-edit-page/cards-add-page.component';
import { OnboardingPageComponent } from './pages/onboarding-page/onboarding-page.component';
import { RedirectGuard } from './redirect.guard';
import { FaqPageComponent } from './pages/faq-page/faq-page.component';
import { PurchaseGuard } from './purchase.guard';
import { ViispPageComponent } from './pages/viisp-page/viisp-page.component';
import { OidcAuthPageComponent } from './pages/oidc-auth-page/oidc-auth-page.component';
import { SignupCompletePageComponent } from './pages/signup-complete-page/signup-complete-page.component';
import { MapPageComponent } from './pages/map-page/map-page.component';
import { PrivacyPageComponent } from './pages/privacy-page/privacy-page.component';
import { ExploreLocationPageComponent } from './pages/explore-page/explore-location-page/explore-location-page.component';
import { ShoppingCartPageV2Component } from './pages/shopping-cart-page-v2/shopping-cart-page.component';
import { PaymentSettingsPageComponent } from './pages/payment-settings-page/payment-settings-page.component';
import { AboutAppComponent } from './shared/components/about-app/about-app.component';
import { PaymentResultPageV2Component } from './pages/payment-result-page-v2/payment-result-page-v2.component';
import { CartGuard } from './cart.guard';
import { JourneyPlannerPurchaseTicketsPageComponent } from './pages/journey-planner-purchase-tickets-page/journey-planner-purchase-tickets-page.component';
import { PurchasePageV2WrapperComponent } from './pages/purchase-page-v2/purchase-page-v2-wrapper/purchase-page-v2-wrapper.component';
import { ExploreRouteGroupPageComponent } from './pages/explore-page/explore-route-group-page/explore-route-group-page.component';
import { TicketQrPageComponent } from './pages/ticket-qr-page/ticket-qr-page.component';
import { AccountMessagesPageComponent } from './pages/account-messages-page/account-messages-page.component';
import { MessagesDetailPageComponent } from './pages/account-messages-page/messages-detail-page/messages-detail-page.component';
import { BlankMobileMenuComponent } from './shared/components/mobile-menu/blank-mobile-menu.component';
import { BannerDetailsPageComponent } from './pages/banner-details-page/banner-details-page.component';
import { ExternalRedirectGuard } from './external-redirect.guard';
import { CorporatePortalPageComponent } from './pages/corporate-portal/corporate-portal.component';
import { RetailersPageComponent } from './pages/retailers-page/retailers-page.component';
import { CemvFaqPageComponent } from './pages/cemv-faq-page/cemv-faq-page.component';
import { OfflineTicketDetailsGuard } from './offline-ticket-details.guard';
import { DepartmentProfileAddComponent } from './pages/department-profile-add/department-profile-add.component';
import { AddNotificationsPageComponent } from './pages/add-notifications-page/add-notifications-page.component';
import { EditSubscriptionsPageComponent } from './pages/edit-subscriptions-page/edit-subscriptions-page.component';
import { EmailConfirmPageComponent } from './pages/email-confirmation/email-confirmation.component';
import { AddSubscriptionsPageComponent } from './pages/add-subscriptions-page/add-subscriptions-page.component';
import { AccountPersonalizePageComponent } from './pages/account-personalize-page/account-personalize-page.component';
import { PersonalizedGuard } from '@ridango/components/lib/auth/guards/personalized.guard';
import { CardInfoPageComponent } from './pages/card-info-page/card-info-page.component';
import { AccessibilityNoticePageComponent } from './pages/accessibility-notice-page/accessibility-notice-page.component';

const routes: Routes = [
    {
        path: '',
        redirectTo: '/home',
        pathMatch: 'full'
    },
    {
        path: 'mobile-menu',
        component: BlankMobileMenuComponent,
        pathMatch: 'full',
        data: { title: 'MOBILE_MENU_PAGE_TITLE' }
    },
    {
        path: 'login',
        component: LoginPageComponent,
        canActivate: [UnauthGuard, AccessGuard, CartGuard],
        pathMatch: 'full',
        data: { title: 'LOG_INTO_TITLE' }
    },
    {
        path: 'home',
        component: HomePageComponent,
        canActivate: [AccessGuard, CartGuard],
        pathMatch: 'full'
    },
    {
        path: 'register',
        component: RegisterPageComponent,
        canActivate: [UnauthGuard, AccessGuard, CartGuard],
        data: { title: 'GET_STARTED_WITH_FREE_ACCOUNT_TITLE' }
    },
    {
        path: 'verify-account/:token',
        component: RegisterVerificationPageComponent,
        canActivate: [UnauthGuard, AccessGuard, CartGuard],
        data: { title: 'PROCESSING_TITLE' }
    },
    {
        path: 'change-email',
        component: EmailVerificationPageComponent,
        canActivate: [AccessGuard, CartGuard],
        data: { title: 'PROCESSING_TITLE' }
    },
    {
        path: 'change-email/:token',
        component: EmailVerificationPageComponent,
        canActivate: [AccessGuard, CartGuard],
        data: { title: 'PROCESSING_TITLE' }
    },
    {
        path: 'email-confirm',
        component: EmailConfirmPageComponent,
        canActivate: [AccessGuard, CartGuard],
        pathMatch: 'full',
        data: { title: 'PROCESSING_TITLE' }
    },
    {
        path: 'purchase/ticket',
        component: PurchasePageComponent,
        canActivate: [AccessGuard, CompanyGuard, PurchaseGuard, CartGuard],
        pathMatch: 'full',
        data: { title: 'PURCHASE_PAGE_TITLE' }
    },
    {
        path: 'purchase/wallet',
        component: WalletLoadingPageComponent,
        canActivate: [AccessGuard, PurchaseGuard, CartGuard],
        pathMatch: 'full',
        data: { title: 'ADD_MONEY', view: 'wallet' }
    },
    {
        path: 'purchase/payment',
        component: ShoppingCartPageV2Component,
        canActivate: [AccessGuard, PurchaseGuard, CartGuard],
        pathMatch: 'full',
        data: { title: 'ADD_MONEY', view: 'payment', paymentFlow: 'top-up' }
    },
    {
        path: 'purchase/garage',
        component: BikeGaragePageComponent,
        canActivate: [AccessGuard, PurchaseGuard, CartGuard],
        pathMatch: 'full',
        data: { title: 'BIKE_GARAGE_TITLE' }
    },
    {
        path: 'purchase-v2/result',
        component: PaymentResultPageV2Component,
        canActivate: [AccessGuard, PurchaseGuard],
        pathMatch: 'full',
        data: { title: 'PURCHASE_RESULT_TITLE_YOUR_TICKET' }
    },
    {
        path: 'purchase-v2/result/ticketId/:ticketId',
        component: PaymentResultPageV2Component,
        canActivate: [AccessGuard, PurchaseGuard],
        pathMatch: 'full',
        data: { title: 'PURCHASE_RESULT_TITLE_YOUR_TICKET' }
    },

    {
        path: 'purchase-v2/result/:bankCardPersonalize',
        component: PaymentResultPageV2Component,
        canActivate: [AccessGuard, PurchaseGuard],
        pathMatch: 'full',
        data: { title: 'PURCHASE_RESULT_TITLE_YOUR_TICKET' }
    },
    {
        path: 'purchase-v2/cart',
        component: ShoppingCartPageV2Component,
        canActivate: [AccessGuard, CompanyGuard, PurchaseGuard, CartGuard],
        pathMatch: 'full',
        data: { title: 'SHOPPING_CART_INTRO_TITLE' }
    },
    {
        path: 'purchase-v2',
        canActivate: [AccessGuard, CompanyGuard, PurchaseGuard, CartGuard],
        data: { title: 'PURCHASE_PAGE_TITLE' },
        children: [
            {
                path: '**',
                component: PurchasePageV2WrapperComponent
            }
        ]
    },
    {
        path: 'customer-support/report',
        component: CustomerSupportPageComponent,
        canActivate: [CartGuard],
        pathMatch: 'full',
        data: { title: 'EMV_SUPPORT_LINK_TITLE' }
    },
    {
        path: 'balance',
        component: BalancePageComponent,
        canActivate: [AccessGuard, CartGuard]
    },
    {
        path: 'viisp',
        component: ViispPageComponent
    },
    {
        path: 'oidc',
        component: OidcAuthPageComponent,
        data: { skipRouteLocalization: true }
    },
    {
        path: 'signup-complete',
        component: SignupCompletePageComponent
    },
    {
        path: 'my-account',
        component: TicketsParentPageComponent,
        canActivate: [CartGuard],
        children: [
            {
                path: '',
                pathMatch: 'full',
                canActivate: [AccessGuard],
                component: BalancePageComponent
            },
            {
                path: 'bankcard-tickets',
                pathMatch: 'full',
                canActivate: [AccessGuard],
                component: BankcardTicketsPageComponent
            }
        ]
    },
    {
        path: 'schedule',
        component: ScheduleParentPageComponent,
        canActivate: [CartGuard],
        children: [
            {
                path: '',
                component: ScheduleFavouritesPageComponent,
                canActivate: [AccessGuard],
                pathMatch: 'full',
                data: { title: 'MAP_TITLE' }
            },
            {
                path: 'search',
                component: ScheduleSearchPageComponent,
                canActivate: [AccessGuard],
                pathMatch: 'full',
                data: { title: 'MAP_TITLE' }
            },
            {
                path: 'planner',
                component: SchedulePlannerPageComponent,
                canActivate: [AccessGuard],
                pathMatch: 'full',
                data: { title: 'MAP_TITLE' }
            },
            {
                path: 'planner/trip/:tripId',
                component: SchedulePlannerTripDetailsPageComponent,
                canActivate: [AccessGuard],
                pathMatch: 'full',
                data: { title: 'MAP_TITLE' }
            },
            {
                path: 'routes',
                component: ScheduleRoutesPageComponent,
                canActivate: [AccessGuard],
                pathMatch: 'full',
                data: { title: 'MAP_TITLE' }
            },
            {
                path: 'routes/:routeId/directions',
                component: ScheduleRouteDirectionsPageComponent,
                canActivate: [AccessGuard],
                pathMatch: 'full',
                data: { title: 'MAP_TITLE' }
            },
            {
                path: 'routes/:routeId/directions/:directionId',
                component: ScheduleRouteStopsPageComponent,
                canActivate: [AccessGuard],
                pathMatch: 'full',
                data: { title: 'MAP_TITLE' }
            },
            {
                path: 'routes/:routeId/directions/:directionId/stops/:stopId',
                component: ScheduleRouteStopDetailsPageComponent,
                canActivate: [AccessGuard],
                pathMatch: 'full',
                data: { title: 'MAP_TITLE' }
            },
            {
                path: 'routes/:routeId/directions/:directionId/stops/:stopId/trips/:tripId',
                component: ScheduleRouteTripPageComponent,
                canActivate: [AccessGuard],
                pathMatch: 'full',
                data: { title: 'MAP_TITLE' }
            }
        ]
    },
    {
        path: 'banner/:bannerId',
        component: BannerDetailsPageComponent,
        canActivate: [AccessGuard]
    },
    {
        path: 'explore',
        component: ExplorePageComponent,
        canActivate: [CartGuard],
        children: [
            {
                path: '',
                component: ExploreSearchPageComponent,
                canActivate: [AccessGuard],
                pathMatch: 'full',
                data: { title: 'EXPLORE_TITLE' }
            },
            {
                path: 'stop/:stopId',
                component: ExploreStopDetailsPageComponent,
                canActivate: [AccessGuard],
                pathMatch: 'full',
                data: { title: 'EXPLORE_STOP_TITLE' }
            },
            {
                path: 'route-group/:name',
                component: ExploreRouteGroupPageComponent,
                canActivate: [AccessGuard],
                pathMatch: 'full',
                data: { title: 'EXPLORE_ROUTE_GROUP_TITLE' }
            },
            {
                path: 'location',
                component: ExploreLocationPageComponent,
                canActivate: [AccessGuard],
                pathMatch: 'full',
                data: { title: 'EXPLORE_LOCATION_TITLE' }
            },
            {
                path: 'trip',
                component: ExploreTripDetailsPageComponent,
                canActivate: [AccessGuard],
                pathMatch: 'full',
                data: { title: 'EXPLORE_TRIP_TITLE' }
            },
            {
                path: 'route/:routeId/patterns',
                component: ExploreRoutePatternsPageComponent,
                canActivate: [AccessGuard],
                pathMatch: 'full',
                data: { title: 'EXPLORE_ROUTE_PATTERNS_TITLE' }
            },
            {
                path: 'pattern/:patternId/schedule/stop/:stopId',
                component: ExploreScheduleOnStopPageComponent,
                canActivate: [AccessGuard],
                pathMatch: 'full',
                data: { title: 'EXPLORE_PATTERN_STOP_TITLE' }
            },
            {
                path: 'pattern/:patternId/schedule/stop/:stopId/additional-info',
                component: ExploreScheduleOnStopAdditionalInfoPageComponent,
                canActivate: [AccessGuard],
                pathMatch: 'full',
                data: { title: 'EXPLORE_PATTERN_STOP_ADDITIONAL_INFO_TITLE' }
            }
        ]
    },
    {
        path: 'journey-planner',
        component: JourneyPlannerPageComponent,
        canActivate: [CartGuard],
        children: [
            {
                path: '',
                component: JourneyPlannerSearchComponent,
                canActivate: [AccessGuard],
                pathMatch: 'full',
                data: { title: 'JOURNEY_PLANNER_TITLE' }
            },
            {
                path: 'result',
                component: JourneyPlannerResultsComponent,
                canActivate: [ExternalRedirectGuard, AccessGuard],
                pathMatch: 'full',
                data: { title: 'JOURNEY_PLANNER_RESULT_TITLE' }
            },
            {
                path: 'itinerary/details',
                component: JourneyPlannerItineraryDetailsComponent,
                canActivate: [AccessGuard],
                pathMatch: 'full',
                data: { title: 'JOURNEY_PLANNER_ITINERARY_DETAILS_TITLE' }
            },
            {
                path: 'purchase',
                children: [{ path: '**', component: JourneyPlannerPurchaseTicketsPageComponent }],
                canActivate: [AccessGuard],
                data: { title: 'PURCHASE_JP' }
            }
        ]
    },
    {
        path: 'map',
        component: MapPageComponent,
        canActivate: [AccessGuard, CartGuard],
        pathMatch: 'full',
        data: { title: 'MAP_TITLE' }
    },
    {
        path: 'purchase/cart',
        component: ShoppingCartPageComponent,
        canActivate: [AccessGuard, PurchaseGuard],
        canDeactivate: [PendingChangesGuard],
        pathMatch: 'full',
        data: { title: 'SHOPPING_CART_INTRO_TITLE' }
    },
    {
        path: 'purchase/result',
        component: PaymentResultPageComponent,
        canActivate: [AccessGuard, PurchaseGuard, CartGuard],
        pathMatch: 'full',
        data: { title: 'PURCHASE_RESULT_TITLE_YOUR_TICKET' }
    },
    {
        path: 'swish',
        component: SwishConfirmationPageComponent,
        canActivate: [AccessGuard, CartGuard],
        canDeactivate: [PendingChangesGuard],
        pathMatch: 'full',
        data: { title: 'PROCESSING_TITLE' }
    },
    {
        path: 'adyen',
        component: AdyenPaymentPageComponent,
        canActivate: [AccessGuard, CartGuard],
        canDeactivate: [PendingChangesGuard],
        pathMatch: 'full',
        data: { title: 'PROCESSING_TITLE' }
    },
    {
        path: 'app-adyen-payment',
        component: AppAdyenPaymentComponent,
        canActivate: [CartGuard],
        pathMatch: 'full',
        data: { title: 'PROCESSING_TITLE' }
    },
    {
        path: 'customer-forget/:token',
        component: CustomerForgetLandingPageComponent,
        canActivate: [AccessGuard, CartGuard],
        pathMatch: 'full'
    },
    {
        path: 'account',
        canActivate: [AuthGuard, RedirectGuard, CartGuard],
        component: AccountParentPageComponent,
        pathMatch: 'full'
    },
    {
        path: 'account',
        component: AccountParentPageComponent,
        canActivate: [AuthGuard, AccessGuard, CartGuard],
        children: [
            {
                path: 'messages',
                children: [
                    {
                        path: '',
                        component: AccountMessagesPageComponent,
                        pathMatch: 'full',
                        data: { title: 'MESSAGES_TITLE' }
                    },
                    {
                        path: ':id',
                        component: MessagesDetailPageComponent,
                        pathMatch: 'full',
                        data: { title: 'MESSAGES_DETAIL_TITLE' }
                    }
                ]
            },
            {
                path: 'tickets',
                canActivate: [TicketDeviceGuard],
                children: [
                    {
                        path: '',
                        component: TicketsPageComponent
                    },
                    {
                        path: ':id/qr',
                        component: TicketQrPageComponent,
                        canActivate: [OfflineTicketDetailsGuard]
                    },
                    {
                        path: ':id',
                        component: TicketDetailsPageComponent,
                        canActivate: [OfflineTicketDetailsGuard],
                        data: { title: 'TICKET_DETAIL_PAGE_TITLE' }
                    }
                ],
                data: { title: 'TICKETS_PAGE_TITLE' }
            },
            {
                path: 'cards',
                component: AccountCardsParentPageComponent,
                children: [
                    {
                        path: '',
                        component: CardsLandingPageComponent,
                        pathMatch: 'full',
                        data: { title: 'TRAVELCARD_LIST_TITLE' }
                    },
                    {
                        path: 'add',
                        component: CardsAddPageComponent,
                        pathMatch: 'full',
                        data: { title: 'TRAVELCARD_LIST_TITLE' }
                    },
                    {
                        path: ':cardId/edit',
                        component: CardsEditPageComponent,
                        pathMatch: 'full',
                        data: { title: 'TRAVELCARD_LIST_TITLE' }
                    },
                    {
                        path: ':cardId/details',
                        component: CardsDetailsPageComponent,
                        pathMatch: 'full',
                        data: { title: 'TRAVELCARD_LIST_TITLE' }
                    }
                ]
            },
            {
                path: 'related-cards',
                component: TravelcardsAndTicketsParentPageComponent,
                canActivate: [CompanyGuard],
                children: [
                    {
                        path: '',
                        component: RelatedCardListPageComponent,
                        pathMatch: 'full',
                        data: { title: 'TRAVELCARD_LIST_TITLE' }
                    },
                    {
                        path: 'add',
                        component: RelatedCardAddAndEditPageComponent,
                        pathMatch: 'full',
                        data: { title: 'ADD_TRAVEL_CARD_SIDE_TITLE' }
                    },
                    {
                        path: ':id',
                        component: RelatedCardDetailPageComponent,
                        pathMatch: 'full',
                        data: { title: 'CARD_DETAILS_TITLE' }
                    },
                    {
                        path: ':id/edit',
                        component: RelatedCardAddAndEditPageComponent,
                        pathMatch: 'full',
                        data: { title: 'EDIT_TRAVEL_CARD_SIDE_TITLE' }
                    }
                ]
            },
            {
                path: 'purchase-history',
                component: PurchaseHistoryPageComponent,
                pathMatch: 'full',
                canActivate: [CompanyGuard],
                data: { title: 'PURCHASE_HISTORY_PAGE_TITLE' }
            },
            {
                path: 'profile',
                component: MyProfileParentPageComponent,
                children: [
                    {
                        path: '',
                        component: AccountDetailPageComponent,
                        pathMatch: 'full',
                        data: { title: 'EDIT_PROFILE' }
                    },
                    {
                        path: 'edit',
                        component: AccountEditPageComponent,
                        pathMatch: 'full',
                        data: { title: 'EDIT_PROFILE' }
                    },
                    {
                        path: 'change-password',
                        component: AccountChangePasswordPageComponent,
                        pathMatch: 'full',
                        data: { title: 'CHANGE_PASSWORD_TITLE' }
                    },
                    {
                        path: 'change-email',
                        component: AccountChangeEmailPageComponent,
                        pathMatch: 'full',
                        data: { title: 'EMAIL_CHANGE' }
                    }
                ]
            },
            {
                path: 'add-notifications',
                component: AddNotificationsPageComponent,
                pathMatch: 'full',
                data: { title: 'ADD_NOTIFICATIONS_PAGE_TITLE' }
            },
            {
                path: 'notification-settings',
                component: NotificationSettingsPageComponent,
                pathMatch: 'full',
                data: { title: 'NOTIFICATION_SETTINGS_PAGE_TITLE' }
            },

            {
                path: 'add-subscriptions',
                component: AddSubscriptionsPageComponent,
                pathMatch: 'full',
                data: { title: 'ADD_SUBSCRIPTIONS_PAGE_TITLE' }
            },
            {
                path: 'edit-subscriptions',
                component: EditSubscriptionsPageComponent,
                pathMatch: 'full',
                data: { title: 'EDIT_SUBSCRIPTIONS_PAGE_TITLE' }
            },
            {
                path: 'validation-history',
                component: ValidationHistoryPageComponent,
                pathMatch: 'full',
                data: { title: 'VALIDATION_HISTORY_PAGE_TITLE' }
            },

            {
                path: 'bookings',
                component: BookingsPageComponent,
                pathMatch: 'full',
                data: { title: 'BOOKINGS_PAGE_TITLE' }
            },
            {
                path: 'ride-history',
                component: RideHistoryPageComponent,
                pathMatch: 'full',
                data: { title: 'PURCHASE_HISTORY_PAGE_TITLE' }
            },
            {
                path: 'seutulippu',
                component: SeutulippuPageComponent,
                pathMatch: 'full'
            },
            {
                path: 'company-users',
                component: AccountCompanyUsersListPageComponent,
                pathMatch: 'full',
                canActivate: [CompanyGuard]
            },
            {
                path: 'company-users/add',
                component: AccountCompanyUsersAddPageComponent,
                pathMatch: 'full',
                canActivate: [CompanyGuard]
            },
            {
                path: 'seutulippu/register',
                component: SeutulippuRegisterPageComponent,
                pathMatch: 'full'
            },
            {
                path: 'switch-company',
                component: AccountCompaniesPageComponent,
                pathMatch: 'full'
            },
            {
                path: 'company-profile',
                component: AccountCompanyEditPageComponent,
                pathMatch: 'full'
            },
            {
                path: 'add-company',
                component: AccountCompanyEditPageComponent,
                pathMatch: 'full'
            },
            {
                path: 'add-department',
                component: DepartmentProfileAddComponent,
                pathMatch: 'full'
            },
            {
                path: 'edit-department',
                component: DepartmentProfileAddComponent,
                pathMatch: 'full'
            },
            {
                path: 'personalize',
                component: AccountPersonalizePageComponent,
                pathMatch: 'full',
                canActivate: [PersonalizedGuard]
            }
        ]
    },
    {
        path: 'terms-and-conditions',
        component: TermsPageComponent,
        canActivate: [CartGuard],
        data: { title: 'TERMS_CONDITIONS_TITLE' }
    },
    {
        path: 'about',
        component: AboutAppComponent,
        canActivate: [CartGuard],
        data: { title: 'ABOUT' }
    },
    {
        path: 'privacy-policy',
        component: PrivacyPageComponent,
        canActivate: [CartGuard],
        data: { title: 'PRIVACY_POLICY_TITLE' }
    },
    {
        path: 'reset-password',
        component: PasswordRecoverPageComponent,
        canActivate: [UnauthGuard, CartGuard],
        data: { title: 'RESET_PASSWORD' }
    },
    {
        path: 'reset-password/:token',
        component: PasswordRecoverVerificationPageComponent,
        canActivate: [CartGuard],
        data: { title: 'RESET_PASSWORD' }
    },
    {
        path: 'set-password/:token',
        component: PasswordRecoverVerificationPageComponent,
        canActivate: [UnauthGuard, CartGuard],
        data: { title: 'FORMER_CUSTOMER_SET_PASSWORD', formerCustomerFirstVisit: true }
    },
    {
        path: 'personalize',
        canActivate: [AccessGuard, CartGuard],
        data: { title: 'PERSONALIZE_TITLE' },
        component: PersonalizeParentPageComponent,
        children: [
            {
                path: '',
                component: RelatedCardPersonalizePageComponent,
                canActivate: [AccessGuard],
                pathMatch: 'full',
                data: { title: 'PERSONALIZE_TITLE' }
            },
            {
                path: 'travelcard',
                component: RelatedCardPersonalizePageComponent,
                canActivate: [AccessGuard],
                pathMatch: 'full',
                data: { title: 'PERSONALIZE_TITLE' }
            },
            {
                path: 'isic-card',
                component: IsicCardActivationPageComponent,
                canActivate: [AccessGuard],
                pathMatch: 'full',
                data: { title: 'ISIC_CARD_ACTIVATION_TITLE' }
            }
        ]
    },
    {
        path: 'bankcard-tickets',
        component: BankcardTicketsPageComponent,
        canActivate: [AccessGuard, CartGuard],
        pathMatch: 'full',
        data: { title: 'BANKCARD_DETAIL_TITLE' }
    },
    {
        path: 'book-a-ride',
        component: BookRideParentPageComponent,
        canActivate: [AuthGuard, CartGuard],
        children: [
            {
                path: '',
                component: BookRidePlanPageComponent,
                pathMatch: 'full',
                data: { title: 'BOOK_RIDE_PLAN_TITLE' }
            },
            {
                path: 'specify',
                component: BookRideSpecifyPageComponent,
                pathMatch: 'full',
                data: { title: 'BOOK_RIDE_SPECIFY_TITLE' }
            },
            {
                path: 'review',
                component: BookRideReviewPageComponent,
                canDeactivate: [PendingChangesGuard],
                pathMatch: 'full',
                data: { title: 'BOOK_RIDE_REVIEW_TITLE' }
            }
        ]
    },
    {
        path: 'feedback',
        component: FeedbackPageComponent,
        canActivate: [AccessGuard, CartGuard],
        pathMatch: 'full',
        data: { title: 'FEEDBACK_PAGE_TITLE' }
    },
    {
        path: 'ondemand-trips',
        component: OndemandTripsPageComponent,
        canActivate: [AccessGuard, CartGuard],
        pathMatch: 'full',
        data: { title: 'ONDEMAND_TRIPS_TITLE' }
    },
    {
        path: 'disruption-messages',
        component: DisruptionMessagesPageComponent,
        canActivate: [AccessGuard, CartGuard],
        pathMatch: 'full',
        data: { title: 'DISRUPTION_MESSAGES_PAGE_TITLE' }
    },
    {
        path: 'faq',
        component: FaqPageComponent,
        canActivate: [AccessGuard, CartGuard],
        pathMatch: 'full'
    },
    {
        path: 'retailers',
        component: RetailersPageComponent,
        canActivate: [AccessGuard, CartGuard],
        pathMatch: 'full'
    },
    {
        path: 'cemv-faq',
        component: CemvFaqPageComponent,
        canActivate: [AccessGuard, CartGuard],
        pathMatch: 'full'
    },
    {
        path: 'card-info',
        component: CardInfoPageComponent,
        canActivate: [AccessGuard, CartGuard],
        pathMatch: 'full'
    },
    {
        path: 'accessibility-notice',
        component: AccessibilityNoticePageComponent,
        canActivate: [AccessGuard, CartGuard],
        pathMatch: 'full'
    },
    {
        path: '**',
        redirectTo: '/purchase/ticket'
    },
    {
        path: 'onboarding',
        component: OnboardingPageComponent,
        canActivate: [AccessGuard],
        pathMatch: 'full',
        data: { title: 'ONBOARDING_PAGE_TITLE' }
    },
    {
        path: 'payment-settings',
        component: PaymentSettingsPageComponent,
        canActivate: [AccessGuard],
        pathMatch: 'full'
    },
    {
        path: 'corporate-portal',
        component: CorporatePortalPageComponent,
        canActivate: [AccessGuard],
        pathMatch: 'full'
    }
];

export function HttpLoaderFactory(
    translate: TranslateService,
    location: Location,
    settings: LocalizeRouterSettings,
    http: HttpClient
) {
    return new LocalizeRouterHttpLoader(translate, location, settings, http);
}

@NgModule({
    imports: [
        LocalizeRouterModule.forRoot(routes, {
            parser: {
                provide: LocalizeParser,
                useFactory: (translate, location, settings, http) =>
                    new LocalizeRouterHttpLoader(translate, location, settings, http, 'assets/settings/locales.json'),
                deps: [TranslateService, Location, LocalizeRouterSettings, HttpClient]
            }
        }),
        RouterModule.forRoot(routes, { relativeLinkResolution: 'legacy' })
    ],
    exports: [RouterModule, LocalizeRouterModule]
})
export class AppRoutingModule {}
